import { useState, useEffect } from 'react'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useRelationshipStore } from '../../../../stores/relationshipStore'
import { usePlotStore } from '../../../../stores/plotStore'
import { useNameStore } from '../../../../stores/nameStore'
import { useTitleStore } from '../../../../stores/titleStore'
import { useWikipediaStore } from '../../../../stores/wikipediaStore'
import { useImagePromptStore } from '../../../../stores/imagePromptStore'
import { useSkillStore } from '../../../../stores/skillStore'
import { useDungeonStore } from '../../../../stores/dungeonStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { useBuildingStore } from '../../../../stores/buildingStore'
import { useLocationStore } from '../../../../stores/locationStore'
import { useDocumentStore } from '../../../../stores/documentStore'
import { useSummaryStore } from '../../../../stores/summaryStore'
import type { ConsultationInput } from '@creanizer/types'

interface GeneratorContextProps {
  onContextChange: (context: ConsultationInput['context']) => void
  criticalMode: boolean
  onCriticalModeChange: (value: boolean) => void
  customContext: string
  onCustomContextChange: (value: string) => void
  avoidElements: string[]
  onAvoidElementsChange: (value: string[]) => void
}

export function GeneratorContext({
  onContextChange,
  criticalMode,
  onCriticalModeChange,
  customContext,
  onCustomContextChange,
  avoidElements,
  onAvoidElementsChange
}: GeneratorContextProps) {
  const [avoidElementsText, setAvoidElementsText] = useState(avoidElements.join('、'))
  const worldStore = useWorldBuilderStore()
  const characterStore = useCharacterStore()
  const itemStore = useItemStore()
  const enemyStore = useEnemyStore()
  const relationshipStore = useRelationshipStore()
  const plotStore = usePlotStore()
  const nameStore = useNameStore()
  const titleStore = useTitleStore()
  const wikipediaStore = useWikipediaStore()
  const imagePromptStore = useImagePromptStore()
  const skillStore = useSkillStore()
  const dungeonStore = useDungeonStore()
  const communityStore = useCommunityStore()
  const societyStore = useSocietyStore()
  const buildingStore = useBuildingStore()
  const locationStore = useLocationStore()
  const documentStore = useDocumentStore()
  const summaryStore = useSummaryStore()
  
  const [primaryWorldId, setPrimaryWorldId] = useState<string>('none')
  const [secondaryWorldId, setSecondaryWorldId] = useState<string>('none')
  const [dataMode, setDataMode] = useState<'selected' | 'none'>('none')
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<Set<string>>(new Set())
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set())
  const [selectedEnemyIds, setSelectedEnemyIds] = useState<Set<string>>(new Set())
  const [selectedRelationshipIds, setSelectedRelationshipIds] = useState<Set<string>>(new Set())
  const [selectedPlotIds, setSelectedPlotIds] = useState<Set<string>>(new Set())
  const [selectedTitleIds, setSelectedTitleIds] = useState<Set<string>>(new Set())
  const [selectedNameIds, setSelectedNameIds] = useState<Set<string>>(new Set())
  const [selectedWikipediaIds, setSelectedWikipediaIds] = useState<Set<string>>(new Set())
  const [selectedImagePromptIds, setSelectedImagePromptIds] = useState<Set<string>>(new Set())
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<string>>(new Set())
  const [selectedDungeonIds, setSelectedDungeonIds] = useState<Set<string>>(new Set())
  const [selectedCommunityIds, setSelectedCommunityIds] = useState<Set<string>>(new Set())
  const [selectedSocietyIds, setSelectedSocietyIds] = useState<Set<string>>(new Set())
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<Set<string>>(new Set())
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set())
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set())
  const [selectedSummaryIds, setSelectedSummaryIds] = useState<Set<string>>(new Set())
  
  useEffect(() => {
    // コンテキストを構築
    const context: ConsultationInput['context'] = {}
    
    // 世界観コンテキスト
    if (primaryWorldId !== 'none') {
      const primaryWorld = worldStore.savedWorlds?.find(w => w.id === primaryWorldId)
      if (primaryWorld) {
        context.worldContext = JSON.stringify(primaryWorld.result)
      }
    }
    
    if (secondaryWorldId !== 'none') {
      const secondaryWorld = worldStore.savedWorlds?.find(w => w.id === secondaryWorldId)
      if (secondaryWorld) {
        context.worldContextSecondary = JSON.stringify(secondaryWorld.result)
      }
    }
    
    // データモードに応じてデータを追加
    if (dataMode === 'selected') {
      // 選択されたデータのみ使用
      context.characters = characterStore.characters.filter(c => selectedCharacterIds.has(c.id))
      context.items = itemStore.items.filter(i => selectedItemIds.has(i.id))
      context.enemies = enemyStore.enemies.filter(e => selectedEnemyIds.has(e.id))
      context.relationships = relationshipStore.savedRelationships?.filter(r => selectedRelationshipIds.has(r.id))
      context.plots = plotStore.savedPlots?.filter(p => selectedPlotIds.has(p.id))
      context.titles = titleStore.savedTitles?.filter(t => selectedTitleIds.has(t.id))
      context.names = nameStore.savedNames?.filter(n => selectedNameIds.has(n.id))
      context.articles = wikipediaStore.savedWikipedias?.filter(w => selectedWikipediaIds.has(w.id))
      context.images = imagePromptStore.savedPrompts?.filter(i => selectedImagePromptIds.has(i.id))
      context.skills = skillStore.skills?.filter(s => selectedSkillIds.has(s.id))
      context.dungeons = dungeonStore.dungeons?.filter(d => selectedDungeonIds.has(d.id))
      context.communities = communityStore.savedCommunities?.filter(c => selectedCommunityIds.has(c.id))
      context.societies = societyStore.savedSocieties?.filter(s => selectedSocietyIds.has(s.id))
      context.buildings = buildingStore.buildings?.filter(b => selectedBuildingIds.has(b.id))
      context.locations = locationStore.locations?.filter(l => selectedLocationIds.has(l.id))
      context.documents = documentStore.documents?.filter(d => selectedDocumentIds.has(d.id))
      context.summaries = summaryStore.summaries?.filter(s => selectedSummaryIds.has(s.id))
    }
    // dataMode === 'none' の場合は何も追加しない
    
    // カスタムコンテキストを追加
    if (customContext && customContext.trim()) {
      context.customContext = customContext
    }
    
    onContextChange(context)
  }, [
    primaryWorldId, 
    secondaryWorldId, 
    dataMode, 
    selectedCharacterIds, 
    selectedItemIds, 
    selectedEnemyIds,
    selectedRelationshipIds,
    selectedPlotIds,
    selectedTitleIds,
    selectedNameIds,
    selectedWikipediaIds,
    selectedImagePromptIds,
    selectedSkillIds,
    selectedDungeonIds,
    selectedCommunityIds,
    selectedSocietyIds,
    selectedBuildingIds,
    selectedLocationIds,
    selectedDocumentIds,
    selectedSummaryIds,
    customContext,
    worldStore.savedWorlds,
    characterStore.characters,
    itemStore.items,
    enemyStore.enemies,
    relationshipStore.savedRelationships,
    plotStore.savedPlots,
    titleStore.savedTitles,
    nameStore.savedNames,
    wikipediaStore.savedWikipedias,
    imagePromptStore.savedPrompts,
    skillStore.skills,
    dungeonStore.dungeons,
    communityStore.savedCommunities,
    societyStore.savedSocieties,
    buildingStore.buildings,
    locationStore.locations,
    documentStore.documents,
    summaryStore.summaries,
    onContextChange
  ])
  
  const toggleCharacterSelection = (id: string) => {
    const newSet = new Set(selectedCharacterIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedCharacterIds(newSet)
  }
  
  const toggleItemSelection = (id: string) => {
    const newSet = new Set(selectedItemIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedItemIds(newSet)
  }
  
  const toggleEnemySelection = (id: string) => {
    const newSet = new Set(selectedEnemyIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedEnemyIds(newSet)
  }
  
  const toggleSelection = <T extends string>(
    selectedSet: Set<T>,
    setSelected: (set: Set<T>) => void,
    id: T
  ) => {
    const newSet = new Set(selectedSet)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelected(newSet)
  }
  
  return (
    <div className="space-y-4">
      {/* 世界観選択（最大2つ） */}
      <div className="space-y-2">
        <Label className="text-xs">世界観設定</Label>
        {!worldStore.savedWorlds || worldStore.savedWorlds.length === 0 ? (
          <div className="text-xs text-muted-foreground p-2 border rounded">
            保存済み世界観がありません。世界観ビルダーで世界観を作成してください。
          </div>
        ) : (
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-muted-foreground">メイン世界観</Label>
            <Select
              value={primaryWorldId}
              onValueChange={setPrimaryWorldId}
            >
              <SelectTrigger>
                <SelectValue placeholder="選択..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">なし</SelectItem>
                {worldStore.savedWorlds?.map((world) => (
                  <SelectItem key={world.id} value={world.id}>
                    {world.result.name || 'Unknown'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-xs text-muted-foreground">比較世界観（任意）</Label>
            <Select
              value={secondaryWorldId}
              onValueChange={setSecondaryWorldId}
              disabled={primaryWorldId === 'none'}
            >
              <SelectTrigger>
                <SelectValue placeholder="比較用..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">なし</SelectItem>
                {worldStore.savedWorlds
                  ?.filter(w => w.id !== primaryWorldId)
                  .map((world) => (
                    <SelectItem key={world.id} value={world.id}>
                      {world.result.name || 'Unknown'}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        )}
        {secondaryWorldId !== 'none' && (
          <p className="text-xs text-blue-500">
            比較検討モード: 2つの世界観を比較分析します
          </p>
        )}
      </div>
      
      {/* データ選択 */}
      <div className="space-y-2">
        <Label className="text-xs">参照データ選択</Label>
        <Tabs value={dataMode} onValueChange={(v) => setDataMode(v as any)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="none">使用しない</TabsTrigger>
            <TabsTrigger value="selected">選択</TabsTrigger>
          </TabsList>
          
          <TabsContent value="selected" className="space-y-2 mt-2">
            {/* キャラクター選択 */}
            <div>
              <Label className="text-xs">キャラクター</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!characterStore.characters || characterStore.characters.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みキャラクターがありません</div>
                ) : (
                  characterStore.characters.map(character => (
                    <div key={character.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`char-${character.id}`}
                        checked={selectedCharacterIds.has(character.id)}
                        onCheckedChange={() => toggleCharacterSelection(character.id)}
                      />
                      <Label 
                        htmlFor={`char-${character.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {character.result.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* アイテム選択 */}
            <div>
              <Label className="text-xs">アイテム</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!itemStore.items || itemStore.items.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みアイテムがありません</div>
                ) : (
                  itemStore.items.map(item => (
                    <div key={item.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`item-${item.id}`}
                        checked={selectedItemIds.has(item.id)}
                        onCheckedChange={() => toggleItemSelection(item.id)}
                      />
                      <Label 
                        htmlFor={`item-${item.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {item.result.basicInfo.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* エネミー選択 */}
            <div>
              <Label className="text-xs">エネミー</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!enemyStore.enemies || enemyStore.enemies.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みエネミーがありません</div>
                ) : (
                  enemyStore.enemies.map(enemy => (
                    <div key={enemy.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`enemy-${enemy.id}`}
                        checked={selectedEnemyIds.has(enemy.id)}
                        onCheckedChange={() => toggleEnemySelection(enemy.id)}
                      />
                      <Label 
                        htmlFor={`enemy-${enemy.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {enemy.result.basicInfo.name || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* 関係性選択 */}
            <div>
              <Label className="text-xs">関係性</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!relationshipStore.savedRelationships || relationshipStore.savedRelationships.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み関係性がありません</div>
                ) : (
                  relationshipStore.savedRelationships.map(rel => {
                    const characters = rel.result.characters || []
                    const displayName = rel.title || 
                      (characters.length >= 2 
                        ? `${characters[0]?.name || 'Unknown'} & ${characters[1]?.name || 'Unknown'}`
                        : characters[0]?.name || 'Unknown')
                    
                    return (
                      <div key={rel.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`rel-${rel.id}`}
                          checked={selectedRelationshipIds.has(rel.id)}
                          onCheckedChange={() => toggleSelection(selectedRelationshipIds, setSelectedRelationshipIds, rel.id)}
                        />
                        <Label 
                          htmlFor={`rel-${rel.id}`}
                          className="text-xs cursor-pointer"
                        >
                          {displayName}
                        </Label>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
            
            {/* プロット選択 */}
            <div>
              <Label className="text-xs">プロット</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!plotStore.savedPlots || plotStore.savedPlots.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みプロットがありません</div>
                ) : (
                  plotStore.savedPlots.map(plot => (
                    <div key={plot.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`plot-${plot.id}`}
                        checked={selectedPlotIds.has(plot.id)}
                        onCheckedChange={() => toggleSelection(selectedPlotIds, setSelectedPlotIds, plot.id)}
                      />
                      <Label 
                        htmlFor={`plot-${plot.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {plot.result.title}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* タイトル選択 */}
            <div>
              <Label className="text-xs">タイトル</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!titleStore.savedTitles || titleStore.savedTitles.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みタイトルがありません</div>
                ) : (
                  titleStore.savedTitles.map(title => (
                    <div key={title.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`title-${title.id}`}
                        checked={selectedTitleIds.has(title.id)}
                        onCheckedChange={() => toggleSelection(selectedTitleIds, setSelectedTitleIds, title.id)}
                      />
                      <Label 
                        htmlFor={`title-${title.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {title.result.candidates?.[0]?.title || 'Unknown'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* 名前選択 */}
            <div>
              <Label className="text-xs">名前</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!nameStore.savedNames || nameStore.savedNames.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み名前がありません</div>
                ) : (
                  nameStore.savedNames.map(name => {
                    const displayName = name.name || 'Unknown'
                    
                    return (
                      <div key={name.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`name-${name.id}`}
                          checked={selectedNameIds.has(name.id)}
                          onCheckedChange={() => toggleSelection(selectedNameIds, setSelectedNameIds, name.id)}
                        />
                        <Label 
                          htmlFor={`name-${name.id}`}
                          className="text-xs cursor-pointer"
                        >
                          {displayName}
                        </Label>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
            
            {/* Wikipedia記事選択 */}
            <div>
              <Label className="text-xs">Wikipedia記事</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!wikipediaStore.savedWikipedias || wikipediaStore.savedWikipedias.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み記事がありません</div>
                ) : (
                  wikipediaStore.savedWikipedias.map(wiki => (
                    <div key={wiki.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`wiki-${wiki.id}`}
                        checked={selectedWikipediaIds.has(wiki.id)}
                        onCheckedChange={() => toggleSelection(selectedWikipediaIds, setSelectedWikipediaIds, wiki.id)}
                      />
                      <Label 
                        htmlFor={`wiki-${wiki.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {wiki.result.title}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* 画像プロンプト選択 */}
            <div>
              <Label className="text-xs">画像プロンプト</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!imagePromptStore.savedPrompts || imagePromptStore.savedPrompts.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み画像プロンプトがありません</div>
                ) : (
                  imagePromptStore.savedPrompts.map(prompt => (
                    <div key={prompt.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`img-${prompt.id}`}
                        checked={selectedImagePromptIds.has(prompt.id)}
                        onCheckedChange={() => toggleSelection(selectedImagePromptIds, setSelectedImagePromptIds, prompt.id)}
                      />
                      <Label 
                        htmlFor={`img-${prompt.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {prompt.result.positivePrompt?.substring(0, 30) || 'Prompt'}...
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* スキル選択 */}
            <div>
              <Label className="text-xs">スキル</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!skillStore.skills || skillStore.skills.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みスキルがありません</div>
                ) : (
                  skillStore.skills.map(skill => (
                    <div key={skill.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`skill-${skill.id}`}
                        checked={selectedSkillIds.has(skill.id)}
                        onCheckedChange={() => toggleSelection(selectedSkillIds, setSelectedSkillIds, skill.id)}
                      />
                      <Label 
                        htmlFor={`skill-${skill.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {skill.result.basicInfo.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* ダンジョン選択 */}
            <div>
              <Label className="text-xs">ダンジョン</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!dungeonStore.dungeons || dungeonStore.dungeons.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みダンジョンがありません</div>
                ) : (
                  dungeonStore.dungeons.map(dungeon => (
                    <div key={dungeon.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`dungeon-${dungeon.id}`}
                        checked={selectedDungeonIds.has(dungeon.id)}
                        onCheckedChange={() => toggleSelection(selectedDungeonIds, setSelectedDungeonIds, dungeon.id)}
                      />
                      <Label 
                        htmlFor={`dungeon-${dungeon.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {dungeon.result.basicInfo.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* コミュニティ選択 */}
            <div>
              <Label className="text-xs">コミュニティ</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!communityStore.savedCommunities || communityStore.savedCommunities.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みコミュニティがありません</div>
                ) : (
                  communityStore.savedCommunities.map(community => (
                    <div key={community.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`community-${community.id}`}
                        checked={selectedCommunityIds.has(community.id)}
                        onCheckedChange={() => toggleSelection(selectedCommunityIds, setSelectedCommunityIds, community.id)}
                      />
                      <Label 
                        htmlFor={`community-${community.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {community.result.basicInfo.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* ソサエティ選択 */}
            <div>
              <Label className="text-xs">ソサエティ</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!societyStore.savedSocieties || societyStore.savedSocieties.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みソサエティがありません</div>
                ) : (
                  societyStore.savedSocieties.map(society => (
                    <div key={society.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`society-${society.id}`}
                        checked={selectedSocietyIds.has(society.id)}
                        onCheckedChange={() => toggleSelection(selectedSocietyIds, setSelectedSocietyIds, society.id)}
                      />
                      <Label 
                        htmlFor={`society-${society.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {society.result.basicInfo.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* 建造物選択 */}
            <div>
              <Label className="text-xs">建造物</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!buildingStore.buildings || buildingStore.buildings.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み建造物がありません</div>
                ) : (
                  buildingStore.buildings.map(building => (
                    <div key={building.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`building-${building.id}`}
                        checked={selectedBuildingIds.has(building.id)}
                        onCheckedChange={() => toggleSelection(selectedBuildingIds, setSelectedBuildingIds, building.id)}
                      />
                      <Label 
                        htmlFor={`building-${building.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {building.result.basicInfo.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* ロケーション選択 */}
            <div>
              <Label className="text-xs">ロケーション</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!locationStore.locations || locationStore.locations.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済みロケーションがありません</div>
                ) : (
                  locationStore.locations.map(location => (
                    <div key={location.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`location-${location.id}`}
                        checked={selectedLocationIds.has(location.id)}
                        onCheckedChange={() => toggleSelection(selectedLocationIds, setSelectedLocationIds, location.id)}
                      />
                      <Label 
                        htmlFor={`location-${location.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {location.result.basicInfo.name}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* 文献選択 */}
            <div>
              <Label className="text-xs">文献</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!documentStore.documents || documentStore.documents.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み文献がありません</div>
                ) : (
                  documentStore.documents.map(doc => (
                    <div key={doc.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`doc-${doc.id}`}
                        checked={selectedDocumentIds.has(doc.id)}
                        onCheckedChange={() => toggleSelection(selectedDocumentIds, setSelectedDocumentIds, doc.id)}
                      />
                      <Label 
                        htmlFor={`doc-${doc.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {(doc.result as any).title || (doc.result as any).headline || (doc.result as any).subject || 'Document'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* 要約選択 */}
            <div>
              <Label className="text-xs">要約</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                {!summaryStore.summaries || summaryStore.summaries.length === 0 ? (
                  <div className="text-xs text-muted-foreground">保存済み要約がありません</div>
                ) : (
                  summaryStore.summaries.map(summary => (
                    <div key={summary.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`summary-${summary.id}`}
                        checked={selectedSummaryIds.has(summary.id)}
                        onCheckedChange={() => toggleSelection(selectedSummaryIds, setSelectedSummaryIds, summary.id)}
                      />
                      <Label 
                        htmlFor={`summary-${summary.id}`}
                        className="text-xs cursor-pointer"
                      >
                        {summary.input.inputType} - {summary.input.fileName || '要約'}
                      </Label>
                    </div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="none" className="mt-2">
            <div className="text-xs text-muted-foreground">
              保存済みデータを参照せずに相談を行います
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* カスタム設定 */}
      <div className="space-y-2">
        <Label className="text-xs">カスタム設定（任意）</Label>
        <Textarea
          value={customContext}
          onChange={(e) => onCustomContextChange(e.target.value)}
          placeholder="追加の設定や文脈情報を入力してください..."
          className="h-24 text-xs"
        />
      </div>

      {/* 禁止ワード */}
      <div className="space-y-2">
        <Label className="text-xs">禁止ワード・表現（任意）</Label>
        <Textarea
          value={avoidElementsText}
          onChange={(e) => setAvoidElementsText(e.target.value)}
          onBlur={() => {
            const words = avoidElementsText
              .split(/[、,，\n]/)
              .map(w => w.trim())
              .filter(w => w.length > 0)
            onAvoidElementsChange(words)
          }}
          placeholder="避けたい単語や表現をカンマか読点で区切って入力（例：血、暴力、死）"
          className="h-16 text-xs"
        />
        <p className="text-xs text-muted-foreground">
          指定した単語や表現を回答に含めないようにします
        </p>
      </div>

      {/* 批評モード */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="criticalMode"
          checked={criticalMode}
          onCheckedChange={onCriticalModeChange}
        />
        <Label htmlFor="criticalMode" className="text-xs cursor-pointer">
          批評モード（より厳しい意見を含む建設的フィードバック）
        </Label>
      </div>
    </div>
  )
}